import {
  FormId,
  InventoryFilterCriteria,
  Lead,
  LeadType,  
} from '../types';

/**
 * Action handler results
 */
export interface ActionResult {
  success: boolean;
  message?: string;
  data?: any;
}

/**
 * Action handlers for Miles AI widget
 */
export class ActionHandlers {
  private apiBase: string;
  private dealerId: string;

  constructor(apiBase: string, dealerId: string) {
    this.apiBase = apiBase;
    this.dealerId = dealerId;
  }

  /**
   * Filter inventory action
   */
  async filterInventory(criteria: InventoryFilterCriteria): Promise<ActionResult> {
    try {
      const response = await fetch(`${this.apiBase}/miles/inventory/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealerId: this.dealerId,
          criteria,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to filter inventory');
      }

      const data = await response.json();

      return {
        success: true,
        message: 'Inventory filtered successfully',
        data,
      };
    } catch (error: any) {
      console.error('[Action Handler] Filter inventory error:', error);
      return {
        success: false,
        message: error.message || 'Failed to filter inventory',
      };
    }
  }

  /**
   * Create lead action
   */
  async createLead(lead: Lead): Promise<ActionResult> {
    try {
      const response = await fetch(`${this.apiBase}/miles/lead/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...lead,
          dealerId: this.dealerId,
          pageUrl: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create lead');
      }

      const data = await response.json();

      return {
        success: true,
        message: 'Lead created successfully',
        data,
      };
    } catch (error: any) {
      console.error('[Action Handler] Create lead error:', error);
      return {
        success: false,
        message: error.message || 'Failed to create lead',
      };
    }
  }

  /**
   * Send SMS action
   */
  async sendSMS(
    phone: string,
    templateId: string,
    payload: Record<string, any>
  ): Promise<ActionResult> {
    try {
      const response = await fetch(`${this.apiBase}/miles/sms/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealerId: this.dealerId,
          phone,
          templateId,
          payload,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send SMS');
      }

      const data = await response.json();

      return {
        success: true,
        message: 'SMS sent successfully',
        data,
      };
    } catch (error: any) {
      console.error('[Action Handler] Send SMS error:', error);
      return {
        success: false,
        message: error.message || 'Failed to send SMS',
      };
    }
  }
}

/**
 * Build inventory filter criteria from natural language
 */
export const buildInventoryFilter = (
  keywords: string[],
  message: string
): InventoryFilterCriteria | null => {
  const criteria: InventoryFilterCriteria = {};
  const lowerMessage = message.toLowerCase();

  // Body style detection
  if (keywords.some(k => ['suv', 'suvs'].includes(k)) || lowerMessage.includes('suv')) {
    criteria.body_style = 'SUV';

    // Size detection for SUVs
    if (lowerMessage.includes('large') || lowerMessage.includes('big')) {
      criteria.size = 'large';
    } else if (lowerMessage.includes('compact') || lowerMessage.includes('small')) {
      criteria.size = 'compact';
    } else if (lowerMessage.includes('midsize') || lowerMessage.includes('mid-size')) {
      criteria.size = 'midsize';
    }
  } else if (keywords.some(k => ['sedan', 'sedans', 'car', 'cars'].includes(k))) {
    criteria.body_style = 'Sedan';
  } else if (keywords.some(k => ['truck', 'trucks', 'pickup'].includes(k))) {
    criteria.body_style = 'Truck';
  } else if (keywords.some(k => ['van', 'vans', 'minivan'].includes(k))) {
    criteria.body_style = 'Van';
  }

  // Fuel type detection
  if (lowerMessage.includes('hybrid')) {
    criteria.fuel_type = 'hybrid';
  } else if (lowerMessage.includes('electric') || lowerMessage.includes('ev')) {
    criteria.fuel_type = 'electric';
  } else if (lowerMessage.includes('diesel')) {
    criteria.fuel_type = 'diesel';
  }

  // Price detection
  const priceMatch = lowerMessage.match(/under\s+\$?([\d,]+)|less than\s+\$?([\d,]+)|below\s+\$?([\d,]+)/);
  if (priceMatch) {
    const price = parseInt(priceMatch[1]?.replace(/,/g, '') || priceMatch[2]?.replace(/,/g, '') || priceMatch[3]?.replace(/,/g, ''));
    if (!isNaN(price)) {
      criteria.max_price = price;
    }
  }

  // Mileage detection
  const mileageMatch = lowerMessage.match(/under\s+([\d,]+)\s*(?:k|thousand)?\s*miles?/);
  if (mileageMatch) {
    let miles = parseInt(mileageMatch[1].replace(/,/g, ''));
    if (lowerMessage.includes('k') || lowerMessage.includes('thousand')) {
      miles *= 1000;
    }
    if (!isNaN(miles)) {
      criteria.max_miles = miles;
    }
  }

  // Seating detection
  if (lowerMessage.includes('7') || lowerMessage.includes('seven')) {
    criteria.seating = '7+';
  } else if (lowerMessage.includes('3 row') || lowerMessage.includes('third row') || lowerMessage.includes('three row')) {
    criteria.seating = '7+';
  }

  // Certified detection
  if (lowerMessage.includes('certified') || lowerMessage.includes('cpo')) {
    criteria.certified = true;
  }

  // Sort detection
  if (lowerMessage.includes('cheapest') || lowerMessage.includes('most affordable') || lowerMessage.includes('lowest price')) {
    criteria.sort = 'price_asc';
  } else if (lowerMessage.includes('newest') || lowerMessage.includes('latest') || lowerMessage.includes('recent')) {
    criteria.sort = 'recent_arrivals';
  } else if (lowerMessage.includes('best mpg') || lowerMessage.includes('fuel efficient') || lowerMessage.includes('gas mileage')) {
    criteria.sort = 'mpg_desc';
  }

  // Return null if no criteria were matched
  return Object.keys(criteria).length > 0 ? criteria : null;
};

/**
 * Determine which form to open based on intent
 */
export const getFormForIntent = (intentId: string): FormId | null => {
  const formMap: Record<string, FormId> = {
    'INT-22': 'form_finance_link',       // Finance application
    'INT-25': 'form_test_drive',         // Test drive
    'INT-26': 'form_trade_appraisal',    // Trade-in (after collecting vehicle info)
    'INT-27': 'form_trade_appraisal',    // Trade value (after collecting vehicle info)
    'INT-28': 'form_contact_request',    // Call me
    'INT-29': 'form_more_photos',        // More photos
    'INT-30': 'form_contact_request',    // Availability + visit
  };

  return formMap[intentId] || null;
};

/**
 * Determine lead type from form ID
 */
export const getLeadTypeFromForm = (formId: FormId): LeadType => {
  const leadTypeMap: Record<FormId, LeadType> = {
    form_test_drive: 'test_drive',
    form_trade_appraisal: 'trade_appraisal',
    form_finance_link: 'finance_request',
    form_contact_request: 'call_request',
    form_more_photos: 'more_photos',
  };

  return leadTypeMap[formId] || 'call_request';
};
